// helix-exception: card surfaces stay hand-rolled divs with card tokens; Helix Card wraps children in an extra padding div and its xl radius resolves to 24px here (F-01)
/**
 * Shared "lights up on hover, click anywhere to edit" summary-card surface
 * (R5 1577:35295). Kept out of component files so React Fast Refresh keeps
 * working (only-export-components ergonomics).
 *
 * Pure summary cards opt in by spreading `CLICKABLE_CARD_SURFACE_CLASS` onto the
 * outer card and wiring the card-level `onClick` to their edit handler. Cards
 * with inner interactive controls (Edit button, expand toggle, badge link) must
 * wrap those handlers with `withStopPropagation` so the click does not also
 * bubble to the card surface and wrongly open the section editor.
 */
import type { MouseEvent } from 'react'

/**
 * Outer-card classes: brand border + elevation on hover, and `group` so the
 * Edit button can fill on card hover.
 */
export const CLICKABLE_CARD_SURFACE_CLASS =
  'group cursor-pointer transition-[border-color,box-shadow] hover:border-brand-600 dark:hover:border-brand-500 hover:shadow-card-hover'

/** `FormCard appearance="status"` surface: brand-softer tint on hover instead of the brand border. */
export const STATUS_CLICKABLE_CARD_SURFACE_CLASS =
  'group cursor-pointer transition-[background-color,box-shadow] hover:bg-background-brand-softer hover:shadow-card-hover dark:hover:bg-brand-950/35'

/** Edit-button classes that fill brand when the parent (`group`) card is hovered. */
export function editButtonHoverFillClass(needsAttention: boolean): string {
  // The `!` (Tailwind v4 important) makes the brand fill beat the button's own
  // hover:bg-background-secondary-medium when the pointer is over the button itself: `group-hover`
  // and `hover` are both active then, and Tailwind emits `group-hover` before
  // `hover`, so without `!important` the light gray bg wins while the text is
  // still forced white — making the button "disappear".
  return needsAttention
    ? 'group-hover:bg-brand-700!'
    : 'group-hover:border-transparent group-hover:bg-brand-600! group-hover:text-white dark:group-hover:text-white dark:group-hover:border-transparent'
}

/**
 * Wraps a handler so a click on an inner control does not also bubble to the
 * clickable card surface (double-fire / wrongly open the section editor).
 */
export function withStopPropagation(handler?: () => void) {
  return (event: MouseEvent) => {
    event.stopPropagation()
    handler?.()
  }
}
